import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UpsellCard from "./UpsellCard";

const ScrollableRow = ({ title, items, onAddToCart }) => {
  const scrollRef = useRef();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const updateScrollButtons = () => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons(); // initial check

    const container = scrollRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [items]);

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -280 : 280;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(updateScrollButtons, 300); // allow scroll to finish
    }
  };

  return (
    <Box sx={{ mb: 4, position: "relative" }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, pl: 1 }}>
        {title}
      </Typography>

      <Box sx={{ position: "relative" }}>
        {/* Left Arrow */}
        {canScrollLeft && (
          <IconButton
            onClick={() => scroll("left")}
            sx={{
              position: "absolute",
              left: 0,
              top: "40%",
              zIndex: 2,
              backgroundColor: "white",
              boxShadow: 1,
              display: { xs: "flex", md: "none" },
            }}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        {/* Scroll Container */}
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            overflowX: "auto",
            py: 1,
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            "& > *": {
              scrollSnapAlign: "start",
              flex: "0 0 auto",
              width: "170px",
              maxWidth: "170px",
              minWidth: "170px",
            },
            position: "relative",
          }}>
          {items.map((item) => (
            <UpsellCard key={item._id} item={item} onAddToCart={onAddToCart} />
          ))}
        </Box>

        {/* Fade shadows */}
        {isMobile && canScrollLeft && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 30,
              zIndex: 1,
              pointerEvents: "none",
              background:
                "linear-gradient(to right, rgba(255,255,255,0.8), rgba(255,255,255,0))",
            }}
          />
        )}

        {isMobile && canScrollRight && (
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 30,
              zIndex: 1,
              pointerEvents: "none",
              background:
                "linear-gradient(to left, rgba(255,255,255,0.8), rgba(255,255,255,0))",
            }}
          />
        )}

        {/* Right Arrow */}
        {canScrollRight && (
          <IconButton
            onClick={() => scroll("right")}
            sx={{
              position: "absolute",
              right: 0,
              top: "40%",
              zIndex: 2,
              backgroundColor: "white",
              boxShadow: 1,
              display: { xs: "flex", md: "none" },
            }}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const UpsellRow = ({ edibleItems = [], accessoryItems = [], onAddToCart }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (edibleItems.length === 0 && accessoryItems.length === 0) return null;

  return (
    <Box sx={{ mt: 8 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        Add a Little Something Extra
      </Typography>

      {isMobile ? (
        <>
          {edibleItems.length > 0 && (
            <ScrollableRow
              title="🍪 Tasty Add-Ons"
              items={edibleItems}
              onAddToCart={onAddToCart}
            />
          )}

          {accessoryItems.length > 0 && (
            <ScrollableRow
              title="🎉 Complete the Celebration"
              items={accessoryItems}
              onAddToCart={onAddToCart}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 4,
            overflowX: "auto",
            py: 1,
            px: 1,
          }}>
          {edibleItems.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1, pl: 1 }}>
                🍪 Tasty Add-Ons
              </Typography>
              <Box sx={{ display: "flex" }}>
                {edibleItems.map((item) => (
                  <UpsellCard
                    key={item._id}
                    item={item}
                    onAddToCart={onAddToCart}
                  />
                ))}
              </Box>
            </Box>
          )}

          {accessoryItems.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1, pl: 1 }}>
                🎉 Complete the Celebration
              </Typography>
              <Box sx={{ display: "flex" }}>
                {accessoryItems.map((item) => (
                  <UpsellCard
                    key={item._id}
                    item={item}
                    onAddToCart={onAddToCart}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UpsellRow;
